import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Route as RouteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { itinerariosAPI, zonasAPI, serviciosAPI } from '../services/api';
import ItinerarioMap from '../components/ItinerarioMap';

const Itinerarios = () => {
  const queryClient = useQueryClient();
  const location = useLocation();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedItinerario, setSelectedItinerario] = useState(null);

  // Form state para generar itinerarios
  const [formData, setFormData] = useState({
    servicio: location.state?.servicio ?? '',
    zona: '',
    fecha: location.state?.fecha ?? new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (location.state?.servicio || location.state?.fecha) {
      setFormData((prev) => ({
        ...prev,
        servicio: location.state.servicio ?? prev.servicio,
        zona: '',
        fecha: location.state.fecha ?? prev.fecha,
      }));
    }
  }, [location.state?.servicio, location.state?.fecha]);

  // Queries
  const { data: itinerariosData, isLoading: itinerariosLoading } = useQuery({
    queryKey: ['itinerarios'],
    queryFn: async () => {
      const response = await itinerariosAPI.listar();
      return response.data;
    },
  });

  const { data: zonas = [], isLoading: zonasLoading } = useQuery({
    queryKey: ['zonas'],
    queryFn: async () => {
      const response = await zonasAPI.listar();
      return response.data.data;
    },
  });

  const { data: servicios = [], isLoading: serviciosLoading } = useQuery({
    queryKey: ['servicios'],
    queryFn: async () => {
      const response = await serviciosAPI.listar();
      return response.data.data;
    },
  });

  const { data: detallesData, isLoading: detallesLoading } = useQuery({
    queryKey: ['itinerario-detalles', selectedItinerario?.Lote],
    queryFn: async () => {
      if (!selectedItinerario) return { data: null };
      const response = await itinerariosAPI.obtener(selectedItinerario.Lote);
      return response.data;
    },
    enabled: !!selectedItinerario && detailsDialogOpen,
  });

  // Mutations
  const generarMutation = useMutation({
    mutationFn: (data) => itinerariosAPI.generar(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['itinerarios']);
      alert(response.data.message || 'Itinerarios generados exitosamente');
      setFormData({
        zona: '',
        servicio: '',
        fecha: new Date().toISOString().split('T')[0],
      });
    },
    onError: (error) => {
      alert(error.response?.data?.error?.message || 'Error al generar itinerarios');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (lote) => itinerariosAPI.eliminar(lote),
    onSuccess: () => {
      queryClient.invalidateQueries(['itinerarios']);
      setDeleteDialogOpen(false);
      setSelectedItinerario(null);
      alert('Itinerario eliminado exitosamente');
    },
    onError: (error) => {
      alert(error.response?.data?.error?.message || 'Error al eliminar itinerario');
    },
  });

  // Handlers
  const handleGenerarItinerarios = () => {
    if (!formData.zona || !formData.servicio) {
      alert('Por favor, seleccione zona y servicio');
      return;
    }
    generarMutation.mutate({
      zona: parseInt(formData.zona),
      servicio: parseInt(formData.servicio),
      fecha: formData.fecha,
    });
  };

  const handleOpenDeleteDialog = (itinerario) => {
    setSelectedItinerario(itinerario);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedItinerario(null);
  };

  const handleOpenDetailsDialog = (itinerario) => {
    setSelectedItinerario(itinerario);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedItinerario(null);
  };

  const handleDelete = () => {
    if (selectedItinerario) {
      deleteMutation.mutate(selectedItinerario.Lote);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatDistancia = (metros) => {
    if (!metros) return '-';
    const km = (metros / 1000).toFixed(2);
    return `${km} km`;
  };

  const formatDuracion = (segundos) => {
    if (!segundos) return '-';
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    return `${horas}h ${minutos}m`;
  };

  const itinerarios = itinerariosData?.data || [];
  const detalles = detallesData?.data || null;

  // Filtrar zonas por servicio seleccionado
  const zonasFiltradas = formData.servicio
    ? zonas.filter(zona => zona.IdServicio === parseInt(formData.servicio))
    : [];

  // Resetear zona cuando cambia el servicio
  const handleServicioChange = (nuevoServicio) => {
    setFormData({
      ...formData,
      servicio: nuevoServicio,
      zona: '', // Resetear zona cuando cambia el servicio
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Generar Itinerarios
      </Typography>

      {/* Form para generar itinerarios */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Generar Nuevos Itinerarios
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                label="Servicio"
                value={formData.servicio}
                onChange={(e) => handleServicioChange(e.target.value)}
                disabled={serviciosLoading}
                helperText={serviciosLoading ? 'Cargando servicios...' : `${servicios.length} servicios disponibles`}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          color: 'text.primary',
                          backgroundColor: 'background.paper',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'action.selected',
                            '&:hover': {
                              backgroundColor: 'action.selected',
                            },
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="" sx={{ color: 'text.secondary' }}>Seleccionar servicio</MenuItem>
                {servicios && servicios.length > 0 ? (
                  servicios.map((servicio, index) => (
                    <MenuItem key={`servicio-${servicio.IdServicio}-${index}`} value={servicio.IdServicio} sx={{ color: 'text.primary' }}>
                      {servicio.NombreServicio}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No hay servicios disponibles</MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                fullWidth
                label="Zona"
                value={formData.zona}
                onChange={(e) => setFormData({ ...formData, zona: e.target.value })}
                disabled={!formData.servicio || zonasLoading}
                helperText={
                  !formData.servicio
                    ? 'Seleccione un servicio primero'
                    : zonasLoading
                      ? 'Cargando zonas...'
                      : `${zonasFiltradas.length} zonas disponibles`
                }
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': {
                          color: 'text.primary',
                          backgroundColor: 'background.paper',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'action.selected',
                            '&:hover': {
                              backgroundColor: 'action.selected',
                            },
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="" sx={{ color: 'text.secondary' }}>Seleccionar zona</MenuItem>
                {zonasFiltradas && zonasFiltradas.length > 0 ? (
                  zonasFiltradas.map((zona, index) => (
                    <MenuItem key={`zona-${zona.IdZona}-${index}`} value={zona.IdZona} sx={{ color: 'text.primary' }}>
                      {zona.NombreZona}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No hay zonas disponibles para este servicio</MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleGenerarItinerarios}
                disabled={generarMutation.isPending}
                startIcon={generarMutation.isPending ? <CircularProgress size={20} /> : <RouteIcon />}
                sx={{ height: '56px' }}
              >
                {generarMutation.isPending ? 'Generando...' : 'Generar'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabla de itinerarios */}
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Itinerarios Generados</Typography>
          <IconButton onClick={() => queryClient.invalidateQueries(['itinerarios'])} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>

        {itinerariosLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : itinerarios.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">No hay itinerarios generados</Alert>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Lote</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Móvil</TableCell>
                  <TableCell>Servicio</TableCell>
                  <TableCell align="center">Recolecciones</TableCell>
                  <TableCell align="right">Distancia</TableCell>
                  <TableCell align="right">Duración</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itinerarios.map((itinerario) => (
                  <TableRow key={itinerario.Lote} hover>
                    <TableCell>
                      <Chip label={itinerario.Lote} size="small" />
                    </TableCell>
                    <TableCell>{formatFecha(itinerario.Fecha)}</TableCell>
                    <TableCell>{itinerario.NombreMovil || `Móvil ${itinerario.Movil}`}</TableCell>
                    <TableCell>{itinerario.Service || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={itinerario.CantidadRecolecciones || 0}
                        color={itinerario.CantidadRecolecciones > 0 ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{formatDistancia(itinerario.Distance)}</TableCell>
                    <TableCell align="right">{formatDuracion(itinerario.Duration)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalles">
                        <IconButton
                          onClick={() => handleOpenDetailsDialog(itinerario)}
                          color="primary"
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          onClick={() => handleOpenDeleteDialog(itinerario)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog de confirmación de eliminación */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar el itinerario <strong>{selectedItinerario?.Lote}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Solo se pueden eliminar itinerarios que no tengan recolecciones ya retiradas.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de detalles del itinerario */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Detalles del Itinerario - {selectedItinerario?.Lote}
        </DialogTitle>
        <DialogContent>
          {detallesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : detalles ? (
            <Box>
              {/* Información general */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Información General</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Móvil</Typography>
                      <Typography variant="body1">{detalles.itinerario.NombreMovil || `Móvil ${detalles.itinerario.Movil}`}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Fecha</Typography>
                      <Typography variant="body1">{formatFecha(detalles.itinerario.Fecha)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Distancia Total</Typography>
                      <Typography variant="body1">{formatDistancia(detalles.itinerario.Distance)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary">Duración Total</Typography>
                      <Typography variant="body1">{formatDuracion(detalles.itinerario.Duration)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Mapa del recorrido */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Mapa del Recorrido</Typography>
                <ItinerarioMap
                  itinerario={detalles.itinerario}
                  steps={detalles.steps}
                  recolecciones={detalles.recolecciones}
                />
              </Box>

              {/* Paradas/Steps */}
              {detalles.steps && detalles.steps.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Paradas del Recorrido</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Orden</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Ubicación</TableCell>
                          <TableCell align="right">Distancia</TableCell>
                          <TableCell align="right">Duración</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detalles.steps.map((step) => (
                          <TableRow key={step.Id}>
                            <TableCell>{step.Orden}</TableCell>
                            <TableCell>
                              <Chip
                                label={step.Type === 'start' ? 'Inicio' : step.Type === 'end' ? 'Fin' : 'Recolección'}
                                size="small"
                                color={step.Type === 'start' ? 'success' : step.Type === 'end' ? 'error' : 'primary'}
                              />
                            </TableCell>
                            <TableCell>{step.Latitud?.toFixed(6)}, {step.Longitud?.toFixed(6)}</TableCell>
                            <TableCell align="right">{formatDistancia(step.Distance)}</TableCell>
                            <TableCell align="right">{formatDuracion(step.Duration)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Recolecciones asignadas */}
              {detalles.recolecciones && detalles.recolecciones.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Recolecciones Asignadas</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Orden</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Dirección</TableCell>
                          <TableCell align="right">Volumen</TableCell>
                          <TableCell>Estado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detalles.recolecciones.map((rec) => (
                          <TableRow key={rec.Id}>
                            <TableCell>{rec.Orden || '-'}</TableCell>
                            <TableCell>{rec.Email}</TableCell>
                            <TableCell>{rec.Direccion || '-'}</TableCell>
                            <TableCell align="right">{rec.Volumen} L</TableCell>
                            <TableCell>
                              <Chip
                                label={rec.FechaRetiro ? 'Retirado' : rec.FechaAnulado ? 'Anulado' : 'Pendiente'}
                                size="small"
                                color={rec.FechaRetiro ? 'success' : rec.FechaAnulado ? 'error' : 'warning'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="error">No se pudieron cargar los detalles</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Itinerarios;
